/**
 * Company › Arche approvals (ADR-022 §7): approve once for every linked
 * workspace (a workspace can still deny for itself), and the "Trust Fias
 * arches" switch over the fixed first-party list — an OWNER's switch, shown
 * read-only to an admin. Decisions come a page at a time ("Show more").
 * An approved arche's activity here is per-workspace counts only: a Company
 * role grants no data access, so who read what stays on each workspace.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { api, type CompanySettingsDto } from '@/lib/api';
import {
  useCompanyApprovals,
  useDecideCompanyApproval,
  useRevokeCompanyApproval,
  useUpdateCompany,
} from '../../hooks/useCompanies';
import { ArcheApprovalDialog } from '../../components/ArcheApprovalDialog';
import { ApprovalsList } from '../../components/ApprovalsList';
import { describeError } from './shared';

export function ApprovalsSection({
  data,
  isOwner,
}: {
  data: CompanySettingsDto;
  isOwner: boolean;
}) {
  return <CompanyApprovalsCard data={data} isOwner={isOwner} />;
}

export function CompanyApprovalsCard({
  data,
  isOwner,
}: {
  data: CompanySettingsDto;
  isOwner: boolean;
}) {
  const companyId = data.company.companyId;
  const companyName = data.company.name;
  const approvals = useCompanyApprovals(companyId);
  const decide = useDecideCompanyApproval(companyId);
  const revoke = useRevokeCompanyApproval(companyId);
  const update = useUpdateCompany(companyId);
  const [dialogOpen, setDialogOpen] = useState(false);
  // A denied row's Approve re-opens the disclosure on that arche.
  const [seedArcheId, setSeedArcheId] = useState<string | null>(null);

  // Turning trust ON approves a whole list at once on every linked
  // workspace — the widest single grant on this page, so it asks first.
  // Turning it OFF only narrows, and goes straight through.
  const [confirmTrust, setConfirmTrust] = useState(false);

  async function setTrust(on: boolean): Promise<void> {
    try {
      await update.mutateAsync({ trustFiasArches: on });
      toast({ title: on ? 'Fias arches trusted' : 'Fias arches no longer trusted' });
    } catch (err) {
      toast({ title: 'Could not save', description: describeError(err), variant: 'destructive' });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Arche approvals</CardTitle>
        <CardDescription>
          An approval here applies to every linked workspace (a workspace can still deny an arche
          for itself). An approval covers exactly the reach it showed: if a new release asks for
          more, the new reach is refused until someone approves again, and revoking is immediate.
          Built-in Fias features (Todo Lists, Calendar, …) follow workspace roles and need no
          approval; approvals and Trust Fias apply to installable arches.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-3">
          <input
            id="trust-fias"
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={data.company.trustFiasArches}
            disabled={update.isPending || !isOwner}
            onChange={(e) => (e.target.checked ? setConfirmTrust(true) : void setTrust(false))}
          />
          <Label htmlFor="trust-fias" className="text-sm leading-tight">
            Trust Fias arches — approve the platform&apos;s own first-party arches, from a fixed
            reviewed list, on every linked workspace. Off until chosen; switchable.
            {!isOwner && (
              <span className="mt-1 block text-xs text-muted-foreground">
                Only an owner can change this.
              </span>
            )}
          </Label>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setSeedArcheId(null);
            setDialogOpen(true);
          }}
        >
          Approve an arche…
        </Button>
        <ApprovalsList
          approvals={approvals.approvals}
          isLoading={approvals.isLoading}
          hasMore={approvals.hasMore}
          isLoadingMore={approvals.isFetchingMore}
          onLoadMore={approvals.loadMore}
          canManage
          fiasTrusted={data.company.trustFiasArches}
          fiasTrustedArches={data.fiasTrustedArches}
          onRevoke={(id) => revoke.mutateAsync(id)}
          onApprove={(archeId) => {
            setSeedArcheId(archeId);
            setDialogOpen(true);
          }}
          activitySource={{
            kind: 'workspaces',
            load: async (archeId) =>
              (await api.getCompanyApprovalActivity(companyId, archeId)).activity,
            workspaceName: (holderId) =>
              data.workspaces.find((w) => w.holderId === holderId)?.displayName ??
              `Workspace …${holderId.slice(-6)}`,
          }}
        />
        <AlertDialog
          open={confirmTrust}
          onOpenChange={(o) => !update.isPending && setConfirmTrust(o)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Trust Fias arches on every linked workspace?</AlertDialogTitle>
              <AlertDialogDescription>
                Every arche on Fias&apos;s fixed, reviewed first-party list
                {data.fiasTrustedArches.length > 0
                  ? ` (today: ${data.fiasTrustedArches.map((a) => a.name).join(', ')})`
                  : ' (empty for now; arches Fias adds later are covered automatically)'}{' '}
                may read each linked workspace&apos;s data on behalf of its members, at their own
                role. Any workspace can still deny one for itself, and you can switch this off at
                any time.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={update.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={update.isPending}
                onClick={(e) => {
                  e.preventDefault();
                  void setTrust(true).then(() => setConfirmTrust(false));
                }}
              >
                {update.isPending ? 'Saving…' : 'Trust Fias arches'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <ArcheApprovalDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          scopeKind="company"
          scopeLabel={companyName}
          initialArcheId={seedArcheId}
          loadDisclosure={async (archeId) =>
            (await api.getCompanyApprovalDisclosure(companyId, archeId)).disclosure
          }
          searchArches={(q) => api.searchCompanyApprovalArches(companyId, q)}
          scopeKey={companyId}
          onDecide={(input) => decide.mutateAsync(input)}
        />
      </CardContent>
    </Card>
  );
}
